import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Sidebar.css';
import {
  LayoutDashboard, Flame, Camera, TreePine, Map, BarChart2, Settings, LogOut, Menu, X, ChevronRight
} from 'lucide-react';

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { icon: <LayoutDashboard size={20} />, label: 'Dashboard', path: '/dashboard' },
    { icon: <Flame size={20} />, label: 'Wildlife Threats', path: '/wildlife-threats' },
    { icon: <Camera size={20} />, label: 'Drone Streaming', path: '/drone-streaming' },
    { icon: <TreePine size={20} />, label: 'IoT Sensing', path: '/iot-sensing' },
    { icon: <BarChart2 size={20} />, label: 'Insights', path: '/insights' },
    { icon: <Map size={20} />, label: 'Drone Map', path: '/drone-map' },
    { icon: <LogOut size={20} />, label: 'Sign Out', path: '/sign-out' }
  ];

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsCollapsed(false);
        setIsOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getBreadcrumbs = () => {
    const currentItem = menuItems.find(item => item.path === location.pathname);
    if (!currentItem) return [];
    
    return [
      { label: 'Home', path: '/' },
      { label: currentItem.label, path: currentItem.path }
    ];
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setIsOpen(!isOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  return (
    <>
      {/* Mobile Menu Button */}
      {isMobile && (
        <button className="fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg" onClick={toggleSidebar}>
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      )}

      {/* Sidebar */}
      <div className={`
        fixed left-0 top-0 h-screen bg-white shadow-lg
        transition-all duration-300 ease-in-out
        ${isCollapsed ? 'w-20' : 'w-60'}
        ${isMobile ? (isOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'}
        z-30
      `}>
        <div className="flex flex-col h-full">
          {/* Desktop Toggle Button */}
          {!isMobile && (
            <button 
              className="absolute right-4 top-4 p-1.5 rounded-lg hover:bg-gray-100"
              onClick={toggleSidebar}
            >
              <Menu size={18} />
            </button>
          )}

          {/* Logo Section */}
          <div className="flex items-center px-4 py-6">
            <img 
              src="../WildAsia-logo.png" 
              alt="WildAsia Logo" 
              className={`${isCollapsed ? 'w-10' : 'w-12'} transition-all duration-300`}
            />
            {!isCollapsed && (
              <span className="ml-3 text-xl font-semibold">WildAsia</span>
            )}
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 px-3">
            {menuItems.map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className={`
                  flex items-center px-3 py-2.5 mb-1 rounded-lg
                  transition-all duration-200
                  ${location.pathname === item.path 
                    ? 'bg-blue-50 text-blue-600' 
                    : 'text-gray-600 hover:bg-gray-100'
                  }
                `}
                onClick={() => isMobile && setIsOpen(false)}
              >
                <span className="flex items-center justify-center">{item.icon}</span>
                {!isCollapsed && (
                  <span className="ml-3 text-sm font-medium">{item.label}</span>
                )}
              </Link>
            ))}
          </nav>

          {/* Footer Section */}
          {!isCollapsed && (
            <div className="p-4 p-4-sidbar border-t">
              <div className="rounded-lg p-4 p-4-sidebar">
                <h5 className="text-sm font-semibold mb-2">WildAsia App</h5>
                <p className="text-xs text-gray-500 mb-3">
                  Get access to all features on our mobile application
                </p>
                <button className="w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
                  Get App
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Overlay for mobile */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;